import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import RAGBadge from "../components/RAGBadge";
import { useTheme } from "../DataContext";
import {
  DEMO_PROJECTS, DEMO_PORTFOLIO_KPIs, generateSPICPITrend
} from "../demoData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";

const fmt = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${(n/1000).toFixed(0)}K`;

export default function PortfolioOverview() {
  const { theme } = useTheme();
  const [filter, setFilter] = useState("all");
  const kpi = DEMO_PORTFOLIO_KPIs;
  const trendData = generateSPICPITrend();

  const filtered = filter === "all" ? DEMO_PROJECTS
    : DEMO_PROJECTS.filter(p => p.rag === filter);

  const ragDist = [
    { name: "On Track",  value: kpi.onTrack,  color: theme.success  },
    { name: "At Risk",   value: kpi.atRisk,   color: theme.warning  },
    { name: "Failing",   value: kpi.failing,  color: theme.danger   },
  ];

  const programData = Object.entries(
    DEMO_PROJECTS.reduce((acc, p) => {
      acc[p.program] = (acc[p.program] || 0) + p.budget;
      return acc;
    }, {})
  ).map(([prog, budget]) => ({ prog: prog.split(" ").slice(0,2).join(" "), budget }))
   .sort((a, b) => b.budget - a.budget);

  return (
    <PageLayout
      title="Portfolio Overview"
      subtitle="Strategic alignment, portfolio health, and executive summary — P-01"
      actions={
        <div style={{ display: "flex", gap: "8px" }}>
          {["all","green","amber","red"].map(f => (
            <button key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: "5px 12px", borderRadius: "6px", border: "1px solid",
                fontSize: "11px", fontWeight: 600, cursor: "pointer",
                letterSpacing: "0.5px", textTransform: "uppercase",
                background: filter === f ? theme.accent : "transparent",
                borderColor: filter === f ? theme.accent : theme.cardBorder,
                color: filter === f ? "#000" : theme.textMuted,
              }}
            >{f === "all" ? "All" : f === "green" ? "On Track" : f === "amber" ? "At Risk" : "Failing"}</button>
          ))}
        </div>
      }
    >
      {/* KPI Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "20px" }}>
        <KPICard label="Total Projects"     value={kpi.totalProjects}     icon="◈" color={theme.accent}  sub="Active portfolio" />
        <KPICard label="Total Budget"       value={fmt(kpi.totalBudget)}  icon="▦" color={theme.cyan}    sub={`Spent: ${fmt(kpi.totalSpent)}`} />
        <KPICard label="Avg Failure Risk"   value={`${kpi.avgFailProb}%`} icon="⚠" color={kpi.avgFailProb > 40 ? theme.danger : theme.warning} sub="ML prediction" trend={4} />
        <KPICard label="Data Quality Score" value={`${kpi.pmDataQuality}%`} icon="◎" color={theme.success} sub="PM compliance" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
        <KPICard label="On Track"    value={kpi.onTrack}  icon="✓" color={theme.success} sub={`${Math.round(kpi.onTrack/kpi.totalProjects*100)}% of portfolio`} />
        <KPICard label="At Risk"     value={kpi.atRisk}   icon="⚠" color={theme.warning} sub="Needs attention" />
        <KPICard label="Failing"     value={kpi.failing}  icon="✗" color={theme.danger}  sub="Intervention required" />
        <KPICard label="Avg CPI"     value={kpi.avgCPI.toFixed(3)} icon="◉" color={kpi.avgCPI >= 1 ? theme.success : theme.warning} sub="Cost Performance Index" />
      </div>

      {/* Charts Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "20px", marginBottom: "24px" }}>
        {/* Portfolio CPI/SPI Trend */}
        <div className="pmo-card">
          <div className="section-title">Portfolio CPI / SPI Trend — 12 Months</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis dataKey="month" tick={{ fill: theme.textMuted, fontSize: 11 }} />
              <YAxis domain={[0.85, 1.05]} tick={{ fill: theme.textMuted, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} labelStyle={{ color: theme.textPrimary }} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              <Line type="monotone" dataKey="avgCPI" stroke={theme.success}  strokeWidth={2} dot={false} name="Avg CPI" />
              <Line type="monotone" dataKey="avgSPI" stroke={theme.cyan}     strokeWidth={2} dot={false} name="Avg SPI" />
              <Line type="monotone" data={trendData.map(d=>({...d, target:1.0}))} dataKey="target" stroke={theme.accent} strokeWidth={1} strokeDasharray="4 4" dot={false} name="Target (1.0)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* RAG Distribution */}
        <div className="pmo-card">
          <div className="section-title">RAG Distribution</div>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={ragDist} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={3} dataKey="value">
                {ragDist.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {ragDist.map(r => (
              <div key={r.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "2px", background: r.color }} />
                  <span style={{ fontSize: "11px", color: theme.textSecondary }}>{r.name}</span>
                </div>
                <span style={{ fontFamily: "'Orbitron',monospace", fontSize: "13px", color: r.color, fontWeight: 700 }}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Budget by Program */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
        <div className="pmo-card">
          <div className="section-title">Budget by Programme</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={programData} layout="vertical" margin={{ top: 0, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}44`} horizontal={false} />
              <XAxis type="number" tickFormatter={v => `$${(v/1000000).toFixed(1)}M`} tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis type="category" dataKey="prog" tick={{ fill: theme.textSecondary, fontSize: 10 }} width={110} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Bar dataKey="budget" fill={theme.accent} radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Benefits Summary */}
        <div className="pmo-card">
          <div className="section-title">Benefits Pipeline</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingTop: "8px" }}>
            {[
              { label: "Total Planned Benefits",    value: fmt(kpi.totalBenefitsPlanned),    color: theme.textSecondary },
              { label: "Total Projected Benefits",  value: fmt(kpi.totalBenefitsProjected),  color: theme.warning },
              { label: "Benefits Gap",              value: fmt(kpi.totalBenefitsPlanned - kpi.totalBenefitsProjected), color: theme.danger },
              { label: "Benefits Realisation Rate", value: "68%",                             color: theme.success },
            ].map(row => (
              <div key={row.label} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "8px 12px", background: theme.bg, borderRadius: "6px",
                border: `1px solid ${theme.cardBorder}44`,
              }}>
                <span style={{ fontSize: "12px", color: theme.textMuted }}>{row.label}</span>
                <span style={{ fontFamily: "'Orbitron',monospace", fontSize: "14px", color: row.color, fontWeight: 600 }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Table */}
      <div className="pmo-card">
        <div className="section-title">Portfolio Register — {filtered.length} Projects</div>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                {["ID","Project Name","Programme","PM","Budget","Spent","% Complete","CPI","SPI","Fail Risk","Status"].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className={p.rag === "red" ? "danger-row" : p.rag === "amber" ? "warning-row" : ""}>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent, fontSize: "12px" }}>{p.id}</td>
                  <td style={{ color: theme.textPrimary, fontWeight: 500 }}>{p.name}</td>
                  <td>{p.program}</td>
                  <td>{p.pm}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{fmt(p.budget)}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{fmt(p.spent)}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <div style={{ flex: 1, height: "4px", background: theme.cardBorder, borderRadius: "2px" }}>
                        <div style={{ width: `${p.pct}%`, height: "100%", background: p.pct > 80 ? theme.success : p.pct > 40 ? theme.accent : theme.warning, borderRadius: "2px" }} />
                      </div>
                      <span style={{ fontSize: "11px", minWidth: "28px" }}>{p.pct}%</span>
                    </div>
                  </td>
                  <td style={{ color: p.cpi >= 1 ? theme.success : p.cpi >= 0.9 ? theme.warning : theme.danger, fontFamily: "'Share Tech Mono',monospace" }}>{p.cpi.toFixed(2)}</td>
                  <td style={{ color: p.spi >= 1 ? theme.success : p.spi >= 0.9 ? theme.warning : theme.danger, fontFamily: "'Share Tech Mono',monospace" }}>{p.spi.toFixed(2)}</td>
                  <td>
                    <span style={{
                      padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700,
                      background: p.failProb > 60 ? `${theme.danger}20` : p.failProb > 30 ? `${theme.warning}20` : `${theme.success}20`,
                      color: p.failProb > 60 ? theme.danger : p.failProb > 30 ? theme.warning : theme.success,
                    }}>{p.failProb}%</span>
                  </td>
                  <td><RAGBadge status={p.rag} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PageLayout>
  );
}
